// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  AltoSpinningLoader,
  Body,
  Button,
  LgPadding,
  ListBase,
  MdSpacing,
  Row,
} from '@alto/design-system';
import React, { useContext } from 'react';
import { formatRelativeDate } from '~shared/helpers/date';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { CHECKOUT_STEPS, useCheckoutInterstitials } from '../../hooks/useCheckoutInterstitials';
import { useEnterCheckoutFlow } from '../../hooks/useEnterCheckoutFlow';

export const EARLIEST_DATE_COMPOUND_ACTION_SHEET_STRINGS = {
  SINGLE_COMPOUND_TEXT:
    'Your cart contains a compound, a medication that is mixed specifically for you according to your prescription and takes an extra few days to arrive via mail. If you need your other medications delivered sooner, remove the following compound medication from your cart and schedule it separately:',
  MULTIPLE_COMPOUNDS_TEXT:
    'Your cart contains compounds, medications that are mixed specifically for you according to your prescription and take an extra few days to arrive via mail. If you need your other medications delivered sooner, remove the following compound medications from your cart and schedule them separately:',
};

/**
 This action sheet is one of the possible interstitials a user may see before navigating to checkout.
 useCheckoutInterstitials is the hook that determines which interstitial/flow to navigate a user to and from.
 It makes that determination by querying the '/enter_checkout' endpoint which assesses the user's cart
 and returns an array of flows and the data needed to power those flows.

 If we're rendering this action sheet it's because 'earliest_date_compound' was returned
 in the checkout_flow_types within the /enter_checkout response.
 **/
export const EarliestDateCompoundActionSheet = () => {
  const { earliestDateCompoundData: data, isLoading } = useEnterCheckoutFlow();
  const earliestAvailableDate = data?.earliest_available_delivery_date;
  const compoundMedicationNames = data?.compound_medication_names;
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const completeCheckoutStep = useCheckoutInterstitials();

  const handleContinue = () => {
    trackEvent({ event: EVENTS.EARLIEST_DATE_COMPOUND_MODAL_CONTINUE_CLICKED });
    completeCheckoutStep(CHECKOUT_STEPS.EARLIEST_DATE_COMPOUND);
    closeActionSheet();
  };
  const handleEditCart = () => {
    trackEvent({ event: EVENTS.EARLIEST_DATE_COMPOUND_MODAL_EDIT_CLICKED });
    closeActionSheet();
  };

  const title = earliestAvailableDate
    ? `Earliest delivery available: ${formatRelativeDate(earliestAvailableDate, { includeRelative: true })}`
    : 'Earlier delivery available';
  const text =
    compoundMedicationNames?.length === 1
      ? EARLIEST_DATE_COMPOUND_ACTION_SHEET_STRINGS.SINGLE_COMPOUND_TEXT
      : EARLIEST_DATE_COMPOUND_ACTION_SHEET_STRINGS.MULTIPLE_COMPOUNDS_TEXT;
  const medicationList =
    compoundMedicationNames?.map((med) => ({
      key: med,
      component: <Body>• {med}</Body>,
    })) || [];

  return (
    <ActionSheetV2
      analyticsName={EVENTS.EARLIEST_DATE_COMPOUND_MODAL_VIEWED}
      buttons={[
        <Button
          key="Continue"
          label="Continue"
          onPress={handleContinue}
        />,
        <Button
          type="tertiary"
          key="Edit cart"
          label="Edit cart"
          onPress={handleEditCart}
        />,
      ]}
      title={title}
    >
      {isLoading ? (
        <LgPadding>
          <Row centerHorizontally>
            <AltoSpinningLoader />
          </Row>
        </LgPadding>
      ) : (
        <LgPadding topPadding={SPACING.STATIC.NONE}>
          <Body>{text}</Body>
          <MdSpacing />
          <ListBase items={medicationList} />
        </LgPadding>
      )}
    </ActionSheetV2>
  );
};
